import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from "react";
import { toast } from "react-toastify";

import {
  VettingCategory,
  VettingProviderProps,
  VettingContextType,
  VettingOption,
  VettingQuestion,
  VettingResponse,
  ComprehensiveQuestion,
  ComprehensiveVettingCategory,
  CategoricalVettingProgress,
} from "./types";
import { AppContext } from "./AppContexts";
import { ca } from "date-fns/locale";

const BaseEndpoint = "https://unido-api.sustainable.co.ke";

// Explicitly state that the context can be undefined outside of the provider
const VettingContext = createContext<VettingContextType | undefined>(undefined);

// Custom hook to access the VettingContext
export const useVettingContext = () => {
  const context = useContext(VettingContext);
  if (context === undefined) {
    throw new Error("useVettingContext must be used within a VettingProvider");
  }
  return context;
};

// Correctly typing the component with React.FC for functional components
export const VettingProvider: React.FC<VettingProviderProps> = ({
  children,
}) => {
  const [questions, setQuestions] = useState<VettingQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [isClearedForAdvanced, setIsClearedForAdvanced] = useState(false);
  const [categoricalData, setCategoricalData] = useState<VettingCategory[]>([]);
  const [answeredQuestions, setAnsweredQUestions] = useState<VettingQuestion[]>(
    []
  );
  const [currentCategoryType, setCurrentCategoryType] =
    useState<string>("basic");

  const { userData, user } = useContext(AppContext);

  const getCategoricalData = async () => {
    const categorical_endpoint = `${BaseEndpoint}/vet/categories/`;

    try {
      const response = await fetch(categorical_endpoint);
      const data = await response.json();
      setCategoricalData(data);
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  const fetchQuestions = async () => {
    try {
      if (!user) {
        console.log('Waiting for user data...');
        return;
      }

      // Detailed logging of userData structure
      console.log('=== User Data Debug ===');
      console.log('Full userData:', JSON.stringify(userData, null, 2));
      console.log('User:', JSON.stringify(user, null, 2));
      console.log('User ID:', user.user_id);
      console.log('=====================');

      const endpoint = `${BaseEndpoint}/vet/vetting-data/?user_id=${user.user_id}`;
      console.log('Making API request to:', endpoint);

      const response = await fetch(endpoint);
      console.log('API Response status:', response.status);
      const data = await response.json();
      console.log('API Response data:', JSON.stringify(data, null, 2));
      setQuestions(data);
    } catch (error) {
      console.error("Error fetching vetting questions:", error);
      toast.error('Failed to load assessment data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getOptions = async (id: number) => {
    const options: VettingOption[] = [];

    try {
      const res = await fetch(`${BaseEndpoint}/vet/options/?question=${id}`);
      const data = await res.json();
      options.push(...data);
      return options;
    } catch (error) {
      console.error("Error fetching options:", error);
      throw error;
    }
  };

  const postUserResponse = async (data: VettingResponse) => {
    const endpoint = BaseEndpoint + "/vet/response/";

    try {
      const res = await fetch(endpoint, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(data),
      });

      toast.success("Response saved successfully");

      let response = await res.json();
      console.log("Successful Answer", response);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getUserResponses = async () => {
    const options: VettingResponse[] = [];

    try {
      const res = await fetch(`${BaseEndpoint}/vet/responses/`);
      const data = await res.json();

      const userResponses = data.filter(
        (datapoint) => datapoint.user == userData?.user_data?.user_id
      );

      options.push(...userResponses);
      return options;
    } catch (error) {
      console.error("Error fetching options:", error);
      throw error;
    }
  };

  const processedForm = async () => {
    try {
      let ctg_data = await getCategoricalData();
      const userResponses = await getUserResponses();
      const allQuestions = ctg_data.flatMap(
        (ctg: VettingCategory) => ctg.category_questions
      );
      const processedQuestions = allQuestions.map(
        (question: VettingQuestion) => {
          const userResponse = userResponses.find(
            (res) => res.question === question.id
          );
          return {
            ...question,
            is_answered: !!userResponse,
            answer_text: userResponse ? userResponse.text || null : null,
            answer_options: userResponse
              ? userResponse.selected_options || []
              : [],
          };
        }
      );
      const processedForm = categoricalData.map((ctg) => {
        const category_questions = processedQuestions.filter(
          (q) => q.category === ctg.id
        );
        const answeredQuestions = category_questions.filter(
          (q) => q.is_answered
        ).length;
        return {
          id: ctg.id,
          category_name: ctg.category_name,
          category_questions,
          category_question_count: ctg.category_question_count,
          answered_questions: answeredQuestions,
        };
      });
      return processedForm;
    } catch (error) {
      console.error("Error processing form data:", error);
      throw error;
    }
  };

  const getComprehensiveForm = async () => {
    try {
      if (!user) {
        console.log('Waiting for user data...');
        return [];
      }

      const progress = await fetch(
        `${BaseEndpoint}/vet/vetting-data/?user_id=${user.user_id}`
      );
      const data = await progress.json();

      let basicAssessmentCategories = [
        "Business Details",
        "Sustainability Journey",
        "Disclosure",
        "About to finish",
        "Signing off",
      ];
      let comprehensiveCategoriesOrder = [
        "Let's Get Started",
        "Before we start - screening",
        "Transparent and Accountable Governance",
        "Workers' Wellbeing",
        "Community Action",
        "Responsible Processing and Packaging",
        "Energy Transition",
        "Nature Regeneration",
        "What's next",
      ];

      // Convert arrays to lowercase for case-insensitive comparison
      basicAssessmentCategories = basicAssessmentCategories.map((name) =>
        name.toLowerCase()
      );
      comprehensiveCategoriesOrder = comprehensiveCategoriesOrder.map((name) =>
        name.toLowerCase()
      );

      const category_data = data[0].data.map((category: any) => {
        return {
          category_id: category.vetting_category,
          category_name: category.vetting_name,
          category_question_count: category.category_question_count,
          answered_questions: category.questions_answered,
          answered_count: category.questions_answered.length,
          questions_unanswered: category.questions_unanswered,
          is_complete: category.questions_unanswered.length === 0,
        };
      });

      // Function to determine the sort order based on category name, case-insensitively
      const getOrder = (categoryName) => {
        categoryName = categoryName.toLowerCase(); // Convert to lowercase for comparison
        let index = basicAssessmentCategories.indexOf(categoryName);
        if (index !== -1) return index;
        index = comprehensiveCategoriesOrder.indexOf(categoryName);
        return index !== -1
          ? index + basicAssessmentCategories.length
          : Number.MAX_SAFE_INTEGER;
      };

      // Sort the categories based on the order defined in basicAssessmentCategories and comprehensiveCategoriesOrder
      category_data.sort((a, b) => {
        let orderA = getOrder(a.category_name);
        let orderB = getOrder(b.category_name);
        return orderA - orderB;
      });

      return category_data;
    } catch (err) {
      console.log(err);
      return [];
    }
  };
  const assortedProgress = async () => {
    try {
      if (!user) {
        console.log('Waiting for user data...');
        return [];
      }

      const progress = await fetch(
        `${BaseEndpoint}/vet/vetting-data/?user_id=${user.user_id}`
      );
      const data = await progress.json();
      
      // Handle case where no application exists
      if (progress.status === 404) {
        console.log('No application found, creating new application...');
        // Create a new application for the user
        const createAppResponse = await fetch(`${BaseEndpoint}/vet/applications/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user: user.user_id,
            status: 'draft'
          })
        });

        if (!createAppResponse.ok) {
          console.error('Failed to create application:', await createAppResponse.text());
          return [];
        }

        // After creating the application, fetch the vetting data again
        const retryProgress = await fetch(
          `${BaseEndpoint}/vet/vetting-data/?user_id=${user.user_id}`
        );
        const retryData = await retryProgress.json();
        
        if (retryProgress.ok && retryData[0]) {
          const clearance = retryData[0]?.cleared_for_advanced_vetting;
          setIsClearedForAdvanced(clearance);
          return retryData[0].data.map((category: any) => ({
            category_id: category.vetting_category,
            category_name: category.vetting_name,
            category_name_sw: category.vetting_name_sw,
            category_question_count: category.category_question_count,
            answered_questions: category.questions_answered.length,
            is_complete: category.questions_unanswered.length === 0,
          }));
        }
        return [];
      }

      if (data[0]) {
        const clearance = data[0]?.cleared_for_advanced_vetting;
        setIsClearedForAdvanced(clearance);
        return data[0].data.map((category: any) => ({
          category_id: category.vetting_category,
          category_name: category.vetting_name,
          category_name_sw: category.vetting_name_sw,
          category_question_count: category.category_question_count,
          answered_questions: category.questions_answered.length,
          is_complete: category.questions_unanswered.length === 0,
        }));
      }
      return [];
    } catch (err) {
      console.error('Error in assortedProgress:', err);
      return [];
    }
  };

  const getApplicationId = async () => {
    if (!user) {
      console.log('Waiting for user data...');
      return null;
    }

    const endpoint = `${BaseEndpoint}/vet/applications?user=${user.user_id}`;
    try {
      const response = await fetch(endpoint, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API request failed with status ${response.status}: ${errorText}`);
      }
      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Expected JSON but got ${contentType}. Response start: ${text.substring(0, 100)}`);
      }
      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error(`Expected array but got ${typeof data}`);
      }
      return data[0]?.id || null;
    } catch (error) {
      console.error('Failed to fetch application ID:', {
        error: error instanceof Error ? error.message : String(error),
        endpoint
      });
      return null;
    }
  };

  const getLatestReportId = async () => {
    if (!user) {
      console.log('Waiting for user data...');
      return null;
    }

    const endpoint = `${BaseEndpoint}/vet/reports/user/${user.user_id}/latest/`;
    try {
      const response = await fetch(endpoint, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      if (!response.ok) {
        if (response.status === 404) {
          console.log('No report found for user');
          return null;
        }
        const errorText = await response.text();
        throw new Error(`API request failed with status ${response.status}: ${errorText}`);
      }
      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Expected JSON but got ${contentType}. Response start: ${text.substring(0, 100)}`);
      }
      const data = await response.json();
      return data?.id || null;
    } catch (error) {
      console.error('Failed to fetch latest report ID:', {
        error: error instanceof Error ? error.message : String(error),
        endpoint
      });
      return null;
    }
  };

  useEffect(() => {
    if (user) {
      fetchQuestions();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      assortedProgress();
    }
  }, [user]);

  return (
    <VettingContext.Provider
      value={{
        questions,
        getCategoricalData,
        getOptions,
        fetchQuestions,
        loading,
        categoricalData,
        postUserResponse,
        processedForm,
        assortedProgress,
        getComprehensiveForm,
        currentCategoryType,
        setCurrentCategoryType,
        isClearedForAdvanced,
        getApplicationId,
        getLatestReportId,
      }}
    >
      {children}
    </VettingContext.Provider>
  );
};
